/**
 * QualityReportPage — rapport Tests & Qualité statique (Chunk 9.R.2).
 *
 * Phase 1 SAFE :
 *   - Rapport STATIQUE versionné dans `apps/web/src/generated/quality-report.json`
 *     importé via `qualityReport.ts` (allowlist EXACTE de 1 fichier).
 *   - Aucune exécution live de tests depuis le navigateur.
 *   - Aucun endpoint backend nouveau.
 *   - Aucun bouton « Run all tests » Phase 1.
 *   - Aucun secret affiché : tous les `command` champs sont des invocations
 *     safe (pas d'env/JWT/token/API key/DB URL).
 *
 * Route : `/internal/quality/tests` sous `ProtectedShellLayout` (logged-in only,
 * parité 9.R.1 `/internal/progress/:slug`).
 *
 * Rendu structuré :
 *   - Header : titre + breadcrumb retour dashboard
 *   - Status global banner (PASS/FAIL/UNKNOWN avec couleur)
 *   - Metadata : commit, branche, generated_at, chunks couverts
 *   - Caveat (si présent) : note honnête sur la couverture
 *   - Workspaces table (shared/api/web/mcp/worker × build/typecheck/tests)
 *   - Legacy budget sentinel (0/0/0 STRICT)
 *   - Smoke staging recent (par chunk)
 *   - CI checks list
 *   - Commands de vérification (safe, sans secrets)
 *   - Phase 1 safety meta
 */

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { cn } from "../../lib/cn";
import { QUALITY_REPORT, type QualityStatus } from "../../lib/qualityReport";

function statusBadgeClass(status: QualityStatus): string {
  if (status === "PASS") {
    return "bg-emerald-50 text-emerald-800 ring-emerald-200";
  }
  if (status === "FAIL") {
    return "bg-red-50 text-red-800 ring-red-200";
  }
  return "bg-amber-50 text-amber-800 ring-amber-200";
}

function StatusBadge({ status }: { status: QualityStatus }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset tabular-nums",
        statusBadgeClass(status),
      )}
      translate="no"
    >
      {status}
    </span>
  );
}

export function QualityReportPage() {
  const { t } = useTranslation();
  const r = QUALITY_REPORT;

  return (
    <div className="mx-auto w-full max-w-5xl">
      <nav aria-label={t("common.breadcrumb")} className="mb-2 text-sm">
        <Link
          to="/"
          className="rounded text-slate-600 hover:text-slate-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 touch-manipulation"
        >
          ← {t("project_progress.page.back_to_dashboard")}
        </Link>
      </nav>

      <h1 className="mb-2 text-xl font-semibold tracking-tight text-slate-900 lg:text-2xl">
        {t("quality.page.title")}
      </h1>
      <p className="mb-4 text-xs text-slate-500">{t("quality.page.subtitle")}</p>

      {/* Status global banner */}
      <section
        className={cn(
          "mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3",
          r.global_status === "PASS"
            ? "border-emerald-200 bg-emerald-50/40"
            : r.global_status === "FAIL"
              ? "border-red-200 bg-red-50/40"
              : "border-amber-200 bg-amber-50/40",
        )}
      >
        <div className="flex items-center gap-3">
          <StatusBadge status={r.global_status} />
          <div>
            <p className="text-sm font-medium text-slate-900">
              {t("quality.page.global_status_label")}
            </p>
            <p className="text-xs text-slate-600">
              {t("quality.page.generated_at")} :{" "}
              <span translate="no">{r.generated_at}</span>
            </p>
          </div>
        </div>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-xs text-slate-700 sm:grid-cols-2">
          <div>
            <dt className="inline font-medium text-slate-500">
              {t("quality.page.branch")} :
            </dt>{" "}
            <dd className="inline font-mono" translate="no">
              {r.branch}
            </dd>
          </div>
          <div>
            <dt className="inline font-medium text-slate-500">
              {t("quality.page.commit")} :
            </dt>{" "}
            <dd className="inline font-mono" translate="no">
              {r.commit}
            </dd>
          </div>
          {r.covered_chunks && r.covered_chunks.length > 0 && (
            <div className="sm:col-span-2">
              <dt className="inline font-medium text-slate-500">
                {t("quality.page.covered_chunks")} :
              </dt>{" "}
              <dd className="inline" translate="no">
                {r.covered_chunks.join(" · ")}
              </dd>
            </div>
          )}
        </dl>
      </section>

      {r.commit_subject && (
        <p className="mb-4 break-words text-xs text-slate-600">
          <span className="font-medium text-slate-500">
            {t("quality.page.commit_subject")} :
          </span>{" "}
          <span className="font-mono" translate="no">
            {r.commit_subject}
          </span>
        </p>
      )}

      {/* Caveat honnête */}
      {r.caveat && (
        <section
          role="note"
          className="mb-4 rounded-xl border border-amber-200 bg-amber-50/40 px-4 py-3 text-sm text-amber-900"
        >
          <p className="font-medium">{t("quality.page.caveat_label")}</p>
          <p className="mt-1 text-amber-800">{r.caveat}</p>
        </section>
      )}

      {/* Workspaces */}
      <section className="mb-6">
        <h2 className="mb-2 text-base font-semibold text-slate-900">
          {t("quality.page.workspaces_title")}
        </h2>
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full border-collapse text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t("quality.page.col_workspace")}
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t("quality.page.col_build")}
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t("quality.page.col_typecheck")}
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t("quality.page.col_tests")}
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t("quality.page.col_notes")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {r.workspaces.map((ws) => (
                <tr key={ws.name} className="align-top hover:bg-slate-50">
                  <th
                    scope="row"
                    className="px-4 py-2 text-left font-mono text-xs font-medium text-slate-800"
                    translate="no"
                  >
                    {ws.name}
                  </th>
                  <td className="px-4 py-2">
                    <StatusBadge status={ws.build} />
                  </td>
                  <td className="px-4 py-2">
                    <StatusBadge status={ws.typecheck} />
                  </td>
                  <td className="px-4 py-2 text-xs tabular-nums text-slate-700">
                    <span className="font-semibold text-slate-900">
                      {ws.tests_passing}
                    </span>
                    <span className="text-slate-400">/{ws.tests_total}</span>
                    {ws.targeted_tests !== undefined && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-brand-50 px-1.5 py-0.5 text-[10px] font-medium text-brand-800 ring-1 ring-inset ring-brand-200">
                        +{ws.targeted_passing ?? ws.targeted_tests}/
                        {ws.targeted_tests} {t("quality.page.targeted_short")}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-600">
                    {ws.notes ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Legacy budget sentinel */}
      {r.legacy_budget && (
        <section className="mb-6">
          <h2 className="mb-2 text-base font-semibold text-slate-900">
            {t("quality.page.legacy_budget_title")}
          </h2>
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
            <div className="mb-2 flex flex-wrap items-center gap-3">
              <span
                className={cn(
                  "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset tabular-nums",
                  r.legacy_budget.status === "STRICT"
                    ? "bg-emerald-50 text-emerald-800 ring-emerald-200"
                    : r.legacy_budget.status === "OK"
                      ? "bg-brand-50 text-brand-800 ring-brand-200"
                      : "bg-red-50 text-red-800 ring-red-200",
                )}
                translate="no"
              >
                {r.legacy_budget.status}
              </span>
              {r.legacy_budget.i18n_fr_en_parity && (
                <span className="text-xs text-slate-600">
                  i18n FR/EN parity :{" "}
                  <span className="font-semibold" translate="no">
                    {r.legacy_budget.i18n_fr_en_parity}
                  </span>
                </span>
              )}
            </div>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-xs text-slate-700 sm:grid-cols-3">
              <div>
                <dt
                  className="inline font-medium text-slate-500"
                  translate="no"
                >
                  {t("quality.page.legacy_label_stone")} :
                </dt>{" "}
                <dd className="inline font-mono tabular-nums" translate="no">
                  {r.legacy_budget.stone_files}/{r.legacy_budget.target_stone}
                </dd>
              </div>
              <div>
                <dt
                  className="inline font-medium text-slate-500"
                  translate="no"
                >
                  {t("quality.page.legacy_label_font_display")} :
                </dt>{" "}
                <dd className="inline font-mono tabular-nums" translate="no">
                  {r.legacy_budget.font_display_files}/
                  {r.legacy_budget.target_font_display}
                </dd>
              </div>
              <div>
                <dt
                  className="inline font-medium text-slate-500"
                  translate="no"
                >
                  {t("quality.page.legacy_label_instrument_serif")} :
                </dt>{" "}
                <dd className="inline font-mono tabular-nums" translate="no">
                  {r.legacy_budget.instrument_serif_files}/
                  {r.legacy_budget.target_instrument_serif}
                </dd>
              </div>
            </dl>
            {r.legacy_budget.notes && (
              <p className="mt-2 text-xs text-slate-600">
                {r.legacy_budget.notes}
              </p>
            )}
          </div>
        </section>
      )}

      {/* Smoke staging récent par chunk */}
      {r.smoke_staging_recent && r.smoke_staging_recent.length > 0 && (
        <section className="mb-6">
          <h2 className="mb-2 text-base font-semibold text-slate-900">
            {t("quality.page.smoke_staging_title")}
          </h2>
          <ul className="space-y-3">
            {r.smoke_staging_recent.map((entry) => (
              <li
                key={entry.chunk}
                className="rounded-xl border border-slate-200 bg-white px-4 py-3"
              >
                <div className="mb-1 flex flex-wrap items-baseline gap-2">
                  <span
                    className="font-mono text-xs font-semibold text-slate-900"
                    translate="no"
                  >
                    {entry.chunk}
                  </span>
                  <span
                    className="text-xs text-slate-500"
                    translate="no"
                  >
                    {entry.date}
                  </span>
                  <span
                    className="font-mono text-[10px] text-slate-400"
                    translate="no"
                  >
                    {entry.head_commit}
                  </span>
                </div>
                <p className="mb-2 text-sm text-slate-800">{entry.summary}</p>
                <ul className="space-y-0.5 text-xs text-slate-600">
                  {entry.surfaces.map((s, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span aria-hidden="true" className="text-emerald-600">
                        ✓
                      </span>
                      <span className="break-words">{s}</span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* CI checks */}
      {r.ci_checks && r.ci_checks.length > 0 && (
        <section className="mb-6">
          <h2 className="mb-2 text-base font-semibold text-slate-900">
            {t("quality.page.ci_checks_title")}
          </h2>
          <ul className="space-y-2">
            {r.ci_checks.map((c, i) => (
              <li
                key={i}
                className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2"
              >
                <StatusBadge status={c.status} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900">{c.name}</p>
                  {c.notes && (
                    <p className="text-xs text-slate-600">{c.notes}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Commands */}
      <section className="mb-6">
        <h2 className="mb-2 text-base font-semibold text-slate-900">
          {t("quality.page.commands_title")}
        </h2>
        <p className="mb-2 text-xs text-slate-600">
          {t("quality.page.commands_hint")}
        </p>
        <ul className="space-y-2">
          {r.commands.map((c, i) => (
            <li
              key={i}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2"
            >
              <p className="text-xs font-medium text-slate-800">{c.label}</p>
              <pre
                className="mt-1 whitespace-pre-wrap break-words rounded bg-slate-50 px-2 py-1 font-mono text-[11px] text-slate-700"
                translate="no"
              >
                {c.command}
              </pre>
              {c.purpose && (
                <p className="mt-1 text-[11px] italic text-slate-500">
                  {c.purpose}
                </p>
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* Phase 1 safety meta */}
      {r.phase1_safety && (
        <section
          aria-label={t("quality.page.safety_title")}
          className="mb-4 rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-xs text-slate-700"
        >
          <p className="mb-2 font-semibold text-slate-800">
            {t("quality.page.safety_title")}
          </p>
          <dl className="space-y-1">
            <div>
              <dt className="inline font-medium text-slate-500">
                {t("quality.page.safety_live_disabled")} :
              </dt>{" "}
              <dd className="inline">
                {r.phase1_safety.live_test_execution_disabled
                  ? t("quality.page.yes")
                  : t("quality.page.no")}
              </dd>
            </div>
            <div>
              <dt className="inline font-medium text-slate-500">
                {t("quality.page.safety_admin_button")} :
              </dt>{" "}
              <dd className="inline">
                {r.phase1_safety.admin_button_run_all_tests}
              </dd>
            </div>
            <div>
              <dt className="inline font-medium text-slate-500">
                {t("quality.page.safety_secrets")} :
              </dt>{" "}
              <dd className="inline">{r.phase1_safety.secrets_in_report}</dd>
            </div>
            <div>
              <dt className="inline font-medium text-slate-500">
                {t("quality.page.safety_source")} :
              </dt>{" "}
              <dd className="inline font-mono" translate="no">
                {r.phase1_safety.report_source}
              </dd>
            </div>
            <div>
              <dt className="inline font-medium text-slate-500">
                {t("quality.page.safety_auth")} :
              </dt>{" "}
              <dd className="inline font-mono" translate="no">
                {r.phase1_safety.route_auth_gating}
              </dd>
            </div>
          </dl>
        </section>
      )}
    </div>
  );
}
